/**
 * Decides whether a hostname should serve the public marketing landing
 * page (PROD-7507) instead of the app.
 *
 *   `designwithme.ai`            → true   (apex)
 *   `www.designwithme.ai`        → true
 *   `3dc.designwithme.ai`        → false  (a client runs the app)
 *   `cty.designwithme.ai`        → false
 *   `localhost` / `127.0.0.1`    → false  (dev keeps the app at `/`;
 *                                          preview the marketing page at `/welcome`)
 *   Firebase preview channels of the apex (…--<channel>.web.app) → true
 *
 * This is intentionally narrow: only the bare apex and its `www` alias are
 * marketing. Everything with a client subdomain is the product. Sibling to
 * `extractSubdomain` (which answers "which client?"); this answers "is this
 * the marketing surface at all?".
 *
 * The marketing route is also always reachable at the explicit `/welcome`
 * path on any host, so localhost and per-client hosts can review it without
 * this guard returning true.
 */

/** The apex domain the marketing site lives on. */
const MARKETING_APEX = 'designwithme.ai';

export function isMarketingHost(hostname: string): boolean {
    if (!hostname) return false;
    const host = hostname.toLowerCase();

    // Bare apex or its www alias.
    if (host === MARKETING_APEX || host === `www.${MARKETING_APEX}`) return true;

    // Firebase Hosting preview channels deploy the apex site to a host like
    // `designwithme-prod--<channel>.web.app`. Treat those as marketing too
    // so QA can review a PR's marketing build. Client-subdomain hosts never
    // match this (they're served from `<client>.designwithme.ai`).
    if (/^designwithme-prod--[a-z0-9-]+\.web\.app$/.test(host)) return true;

    return false;
}
